// ─── src/theme/ThemeContext.jsx ───────────────────────────────────────────────
// Single source of truth for dark/light mode across the entire app.
// Persists user preference in localStorage so it survives navigation + refresh.

import { createContext, useContext, useState, useMemo, useCallback, useEffect } from "react";
import { getDarkPalette, getLightPalette, getDefaultMode, setUserMode } from "./tokens";
import { applyThemeToDocument } from "./ThemeLoader";

const ThemeCtx = createContext(null);

export { ThemeCtx };

export function ThemeProvider({ children }) {
  const [darkMode, setDarkMode] = useState(() => getDefaultMode() === "dark");

  const toggleDark = useCallback(() => {
    setDarkMode((prev) => {
      const next = !prev;
      setUserMode(next ? "dark" : "light");
      return next;
    });
  }, []);

  // Keep CSS variables and data-lwd-mode in sync whenever darkMode changes
  useEffect(() => {
    applyThemeToDocument();
  }, [darkMode]);

  // Fetch admin-saved default from Supabase, applies only if the user
  // hasn't explicitly toggled their own preference.
  useEffect(() => {
    const userPref = (() => { try { return localStorage.getItem("lwd_user_dark_mode"); } catch { return null; } })();
    if (userPref === "dark" || userPref === "light") return; // user choice wins
    import("../lib/supabaseClient").then(({ supabase }) => {
      supabase
        .from("platform_settings")
        .select("value")
        .eq("key", "default_dark_mode")
        .eq("category", "design")
        .maybeSingle()
        .then(({ data }) => {
          if (data?.value === "dark" || data?.value === "light") {
            // Sync to localStorage so the next page load starts with the correct
            // mode from the beginning (prevents the async-fetch flash).
            try {
              const existing = JSON.parse(localStorage.getItem("lwd_theme_overrides")) ?? {};
              if ((existing?.site?.defaultMode) !== data.value) {
                existing.site = { ...(existing.site || {}), defaultMode: data.value };
                localStorage.setItem("lwd_theme_overrides", JSON.stringify(existing));
              }
            } catch {}
            setDarkMode(data.value === "dark");
          }
        });
    }).catch(() => {});
  }, []);

  // Spread palette into context so `const C = useTheme()` still works
  // C.gold, C.white etc. plus C.darkMode, C.toggleDark, C.setDarkMode
  const value = useMemo(() => {
    const palette = darkMode ? getDarkPalette() : getLightPalette();
    return { ...palette, darkMode, setDarkMode, toggleDark };
  }, [darkMode, toggleDark]);

  return <ThemeCtx.Provider value={value}>{children}</ThemeCtx.Provider>;
}

/** Drop-in hook, returns palette + { darkMode, setDarkMode, toggleDark } */
export const useTheme = () => useContext(ThemeCtx) || {};
